import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import type { CycleCallback } from '@/components/ui/Timeline';
import type { BeraReportSegmentPayload } from '@/components/views/BeraJob/hooks/types/request';
import type { BeraReportV2, BeraReportV2Segment } from '@/types/Bera';

dayjs.extend(customParseFormat);

const SEGMENT_TIME_FORMAT = 'HH:mm:ss.SSS';

export interface StretchRange {
	start: number;
	end: number;
}
const FIRST_SEQUENCE = 1;
const MEDIA_FRAGMENT_START = '#t=';

function toSegmentTime(value: Dayjs): string {
	const result = value.format(SEGMENT_TIME_FORMAT);

	return result;
}

function toSeconds(value: Dayjs): number {
	const result = value.diff(value.startOf('day'), 'second', true);

	return result;
}

export function fromSegmentTime(value: string): Dayjs {
	const result = dayjs(value, SEGMENT_TIME_FORMAT);

	return result;
}

export function toSegmentsPayload(stretches: CycleCallback[]): BeraReportSegmentPayload[] {
	const chronological = [...stretches].sort(
		(first, second) => first.start_time.valueOf() - second.start_time.valueOf()
	);
	const result = chronological.map((stretch, index) => ({
		segment_start: toSegmentTime(stretch.start_time),
		segment_end: toSegmentTime(stretch.end_time),
		sequence: index + FIRST_SEQUENCE
	}));

	return result;
}

export function toStretches(segments: BeraReportV2Segment[], taskId: string): CycleCallback[] {
	const result = segments.map((segment) => ({
		id: segment.id,
		type_id: taskId,
		start_time: fromSegmentTime(segment.segment_start),
		end_time: fromSegmentTime(segment.segment_end),
		identified: true,
		is_persisted: true
	}));

	return result;
}

export function toRangesFromStretches(stretches: CycleCallback[]): StretchRange[] {
	const ranges = stretches.map((stretch) => ({
		start: toSeconds(stretch.start_time),
		end: toSeconds(stretch.end_time)
	}));
	const result = ranges.sort((first, second) => first.start - second.start);

	return result;
}

export function toStretchesFromReports(reports: BeraReportV2[]): CycleCallback[] {
	const byReport = reports.map((report) => toStretches(report.segments ?? [], report.task_id));
	const result = byReport.flat();

	return result;
}

export function hasPatchedSegment(report: BeraReportV2): boolean {
	const segments = report.segments ?? [];
	const result = segments.some((segment) => !!segment.source_file_id);

	return result;
}

function toStartInSeconds(segment: BeraReportV2Segment): number {
	const start = fromSegmentTime(segment.segment_start);
	const result = toSeconds(start);

	return result;
}

export function toFirstStretchFrameUrl(videoUrl: string, report: BeraReportV2): string {
	const segments = report.segments ?? [];

	if (!segments.length) {
		return videoUrl;
	}

	const starts = segments.map(toStartInSeconds);
	const firstStart = Math.min(...starts);
	const result = `${videoUrl}${MEDIA_FRAGMENT_START}${firstStart}`;

	return result;
}

interface SummaryScopeParams {
	summaryId: string;
	organizationId: string;
	companyId: string;
}

export interface SummaryScope {
	id: string;
	organization_id: string;
	company_id: string;
}

export function toSummaryScope({ summaryId, organizationId, companyId }: SummaryScopeParams): SummaryScope {
	const result = { id: summaryId, organization_id: organizationId, company_id: companyId };

	return result;
}
